import { useEffect, useState } from "react";
import { KeyRound, Send, Loader2, Copy, Check, Eye, EyeOff, MessageCircle, ExternalLink, Power } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { fetchWaSignature, appendWaSignature } from "../lib/waLink";
import { toWaNumber } from "./WhatsAppLink";

const copyText = async (v) => {
  try { await navigator.clipboard.writeText(v); } catch (e) {
    const ta = document.createElement("textarea");
    ta.value = v; document.body.appendChild(ta); ta.select(); document.execCommand("copy"); ta.remove();
  }
};

const CurrentPassword = ({ anagId, version }) => {
  const [pwd, setPwd] = useState(null);
  const [copied, setCopied] = useState(false);
  useEffect(() => { setPwd(null); }, [version]);
  const toggle = async () => {
    if (pwd !== null) { setPwd(null); return; }
    try { const { data } = await api.get(`/anagrafiche/clienti/${anagId}/area-account/password`); setPwd(data.password); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const copy = async () => { await copyText(pwd); setCopied(true); setTimeout(() => setCopied(false), 2000); };
  return (
    <div className="flex items-center gap-2" data-testid="area-riservata-current-password">
      <span className="text-xs text-slate-600">Password attuale:</span>
      <code className="rounded-md border border-slate-300 bg-white px-2 py-1 text-xs text-slate-800 min-w-[110px]" data-testid="area-riservata-current-password-value">
        {pwd === null ? "••••••••" : pwd || "non disponibile"}
      </code>
      <button type="button" onClick={toggle} title={pwd === null ? "Mostra password" : "Nascondi password"} data-testid="area-riservata-show-password"
        className="p-1.5 rounded-md border border-slate-300 bg-white text-slate-700 hover:bg-slate-100 transition-colors">
        {pwd === null ? <Eye className="h-3.5 w-3.5" /> : <EyeOff className="h-3.5 w-3.5" />}
      </button>
      {pwd && (
        <button type="button" onClick={copy} title="Copia password" data-testid="area-riservata-copy-password"
          className="p-1.5 rounded-md border border-slate-300 bg-white text-slate-700 hover:bg-slate-100 transition-colors">
          {copied ? <Check className="h-3.5 w-3.5 text-green-600" /> : <Copy className="h-3.5 w-3.5" />}
        </button>
      )}
      {pwd === "" && <span className="text-[11px] text-slate-500">Imposta una nuova password per poterla vedere.</span>}
    </div>
  );
};

// Cache per apertura immediata della sezione Area Riservata nella scheda anagrafica.
const accCache = new Map();
const inflight = new Map();
export const prefetchAreaAccount = (anagId) => {
  if (!anagId) return Promise.resolve(null);
  if (inflight.has(anagId)) return inflight.get(anagId);
  const pr = api.get(`/anagrafiche/clienti/${anagId}/area-account`)
    .then(({ data }) => { accCache.set(anagId, data); return data; })
    .finally(() => inflight.delete(anagId));
  inflight.set(anagId, pr);
  return pr;
};

const PraticheCollegate = ({ acc }) => {
  const [open, setOpen] = useState(false);
  const list = acc.pratiche_list || [];
  if (!acc.exists) return <span className="text-[11px] font-semibold rounded-full px-2 py-0.5 bg-slate-200 text-slate-600" data-testid="area-riservata-status">Nessun account</span>;
  const off = acc.attivo === false;
  return (
    <div className="relative">
      <button type="button" onClick={() => setOpen((v) => !v)} disabled={!list.length} data-testid="area-riservata-status"
        title={list.length ? "Mostra le pratiche collegate" : ""}
        className={`text-[11px] font-semibold rounded-full px-2 py-0.5 transition-colors disabled:cursor-default ${off ? "bg-red-100 text-red-700 hover:bg-red-200" : "bg-green-100 text-green-700 hover:bg-green-200"}`}>
        {off ? "Area disattivata" : "Account attivo"} · {acc.pratiche} pratiche collegate{list.length ? (open ? " ▴" : " ▾") : ""}
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-1 z-20 w-72 max-h-64 overflow-y-auto rounded-md border border-slate-300 bg-white shadow-lg py-1" data-testid="area-riservata-pratiche-list">
          {list.map((p) => (
            <a key={p.id} href={`/app/pratiche?p=${p.id}`} target="_blank" rel="noopener noreferrer" data-testid={`area-riservata-pratica-${p.id}`}
              className="flex items-center justify-between gap-2 px-3 py-1.5 text-sm text-slate-800 hover:bg-amber-50 transition-colors">
              <span className="font-mono font-semibold">{p.numero_pratica || "—"}</span>
              <span className="text-[11px] text-slate-500 truncate">{[p.targa, (p.stato || "").replace(/_/g, " ")].filter(Boolean).join(" · ")}</span>
              <ExternalLink className="h-3.5 w-3.5 text-slate-400 shrink-0" />
            </a>
          ))}
        </div>
      )}
    </div>
  );
};

export const AreaRiservataPanel = ({ anagId, defaultEmail }) => {
  const cached = accCache.get(anagId) || null;
  const [acc, setAcc] = useState(cached);
  const [email, setEmail] = useState(cached ? (cached.email || defaultEmail || "") : "");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState("");
  const [copied, setCopied] = useState(false);
  const [welcome, setWelcome] = useState(true);
  const [ver, setVer] = useState(0);
  const [pickWa, setPickWa] = useState(false);
  const areaUrl = `${window.location.origin}/cliente`;
  const copyLink = async () => {
    await copyText(areaUrl);
    setCopied(true); setTimeout(() => setCopied(false), 2000);
  };
  const load = () => prefetchAreaAccount(anagId).then((data) => { if (data) { setAcc(data); setEmail((cur) => cur || data.email || defaultEmail || ""); } }).catch(() => {});
  useEffect(() => { load(); }, [anagId]); // eslint-disable-line react-hooks/exhaustive-deps
  const run = async (kind, fn) => {
    setBusy(kind);
    try { await fn(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(""); }
  };
  const save = () => run("save", async () => {
    const { data } = await api.post(`/anagrafiche/clienti/${anagId}/area-account`, { email, password, send_welcome: !acc?.exists && welcome, app_url: window.location.origin });
    toast.success(acc?.exists ? "Account aggiornato" : data.welcome_sent ? `Account creato, email di benvenuto inviata a ${data.email}` : "Account Area Riservata creato");
    setPassword(""); setVer((v) => v + 1); accCache.delete(anagId); setEmail(""); load();
  });
  const magic = () => run("magic", async () => {
    const { data } = await api.post(`/anagrafiche/clienti/${anagId}/area-account/magic-link`, { app_url: window.location.origin });
    toast.success(`Link di accesso inviato a ${data.email}`);
    accCache.delete(anagId); load();
  });
  const magicWa = (numero) => {
    setPickWa(false);
    const win = window.open("", "_blank");
    return run("wa", async () => {
      try {
        const { data } = await api.post(`/anagrafiche/clienti/${anagId}/area-account/magic-link-wa`, { app_url: window.location.origin, numero });
        const text = appendWaSignature(`Ciao ${data.name},\necco il link per accedere alla tua Area Riservata (valido ${data.hours} ore, utilizzabile una sola volta):\n${data.link}`, await fetchWaSignature());
        const url = `https://wa.me/${toWaNumber(data.numero)}?text=${encodeURIComponent(text)}`;
        if (win) { win.opener = null; win.location.href = url; } else window.open(url, "_blank", "noopener,noreferrer");
        toast.success(`Link di accesso pronto su WhatsApp per ${data.numero}`);
        accCache.delete(anagId); load();
      } catch (e) { if (win) win.close(); throw e; }
    });
  };
  const toggleStato = () => run("stato", async () => {
    const attivo = acc.attivo === false;
    if (!attivo && !window.confirm("Disattivare l'Area Riservata? Il cliente verrà scollegato e non potrà più accedere finché non la riattivi.")) return;
    await api.post(`/anagrafiche/clienti/${anagId}/area-account/stato`, { attivo });
    toast.success(attivo ? "Area Riservata riattivata" : "Area Riservata disattivata");
    accCache.delete(anagId); load();
  });
  const noEnter = (e) => { if (e.key === "Enter") e.preventDefault(); };
  if (!acc) return (
    <div className="rounded-lg border border-amber-300 bg-amber-50/60 p-4 flex items-center gap-2 text-xs text-amber-800" data-testid="area-riservata-loading">
      <Loader2 className="h-3.5 w-3.5 animate-spin" /> Caricamento Area Riservata…
    </div>
  );
  return (
    <div className="rounded-lg border border-amber-300 bg-amber-50/60 p-4 space-y-3" data-testid="area-riservata-panel">
      <div className="flex items-center justify-between">
        <div className="text-xs font-bold uppercase tracking-widest text-amber-800">Area Riservata cliente</div>
        <PraticheCollegate acc={acc} />
      </div>
      <div className="grid sm:grid-cols-2 gap-3">
        <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} onKeyDown={noEnter} placeholder="Email di accesso"
          data-testid="area-riservata-email" className="h-9 rounded-md border border-slate-300 bg-white px-3 text-sm focus:outline-none focus:border-amber-500" />
        <input type="text" value={password} onChange={(e) => setPassword(e.target.value)} onKeyDown={noEnter}
          placeholder={acc.exists ? "Nuova password (opzionale)" : "Password iniziale (min. 8)"}
          data-testid="area-riservata-password" className="h-9 rounded-md border border-slate-300 bg-white px-3 text-sm focus:outline-none focus:border-amber-500" />
      </div>
      {acc.exists && <CurrentPassword anagId={anagId} version={ver} />}
      <div className="flex flex-wrap gap-2">
        <button type="button" onClick={save} disabled={!!busy} data-testid="area-riservata-save"
          className="flex items-center gap-1.5 rounded-md bg-amber-500 hover:bg-amber-600 px-3 py-1.5 text-xs font-semibold text-[#0a0d14] transition-colors disabled:opacity-60">
          {busy === "save" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <KeyRound className="h-3.5 w-3.5" />}{acc.exists ? "Aggiorna account" : "Crea account"}
        </button>
        {!acc.exists && (
          <label className="flex items-center gap-1.5 text-xs text-slate-700 cursor-pointer select-none">
            <input type="checkbox" checked={welcome} onChange={(e) => setWelcome(e.target.checked)} data-testid="area-riservata-welcome" className="accent-amber-500" />
            Invia email di benvenuto
          </label>
        )}
        {acc.exists && (
          <button type="button" onClick={toggleStato} disabled={!!busy} data-testid="area-riservata-toggle-stato"
            className={`flex items-center gap-1.5 rounded-md border px-3 py-1.5 text-xs font-semibold transition-colors disabled:opacity-60 ${acc.attivo === false ? "border-green-600 text-green-700 hover:bg-green-50" : "border-red-500 text-red-700 hover:bg-red-50"}`}>
            {busy === "stato" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Power className="h-3.5 w-3.5" />}{acc.attivo === false ? "Riattiva Area Riservata" : "Disattiva Area Riservata"}
          </button>
        )}
        {acc.exists && acc.attivo !== false && (
          <button type="button" onClick={magic} disabled={!!busy} data-testid="area-riservata-magic"
            className="flex items-center gap-1.5 rounded-md border border-amber-500 px-3 py-1.5 text-xs font-semibold text-amber-800 hover:bg-amber-100 transition-colors disabled:opacity-60">
            {busy === "magic" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}Invia link di accesso
          </button>
        )}
        {acc.exists && acc.attivo !== false && (
          <div className="relative">
          <button type="button" onClick={() => ((acc.numeri || []).length > 1 ? setPickWa((v) => !v) : magicWa((acc.numeri || [])[0]))} disabled={!!busy} data-testid="area-riservata-magic-wa"
            className="flex items-center gap-1.5 rounded-md border border-[#25D366] px-3 py-1.5 text-xs font-semibold text-[#128C7E] hover:bg-[#25D366]/10 transition-colors disabled:opacity-60">
            {busy === "wa" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <MessageCircle className="h-3.5 w-3.5" />}Invia link via WhatsApp
          </button>
          {pickWa && (
            <div className="absolute left-0 top-full mt-1 z-20 min-w-[220px] rounded-md border border-slate-300 bg-white shadow-lg py-1" data-testid="area-riservata-wa-picker">
              <div className="px-3 py-1 text-[11px] font-semibold uppercase tracking-wider text-slate-500">Scegli il numero</div>
              {acc.numeri.map((n, i) => (
                <button key={n} type="button" onClick={() => magicWa(n)} data-testid={`area-riservata-wa-num-${i}`}
                  className="w-full text-left px-3 py-1.5 text-sm text-slate-800 hover:bg-[#25D366]/10 flex items-center gap-2 transition-colors">
                  <MessageCircle className="h-3.5 w-3.5 text-[#25D366]" />{n}
                </button>
              ))}
            </div>
          )}
          </div>
        )}
      </div>
      <div className="flex items-center gap-2">
        <code className="flex-1 truncate rounded-md border border-slate-300 bg-white px-3 py-1.5 text-xs text-slate-700" data-testid="area-riservata-link">{areaUrl}</code>
        <button type="button" onClick={copyLink} data-testid="area-riservata-copy-link"
          className="flex items-center gap-1 rounded-md border border-slate-300 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-100 transition-colors">
          {copied ? <Check className="h-3.5 w-3.5 text-green-600" /> : <Copy className="h-3.5 w-3.5" />}{copied ? "Copiato!" : "Copia"}
        </button>
      </div>
      {acc.exists && (
        <div className="text-xs text-slate-700" data-testid="area-riservata-ultimo-accesso">
          <span className="font-semibold text-amber-800">Ultimo accesso del cliente:</span>{" "}
          {acc.ultimo_accesso ? `${new Date(acc.ultimo_accesso).toLocaleDateString("it-IT")} alle ${new Date(acc.ultimo_accesso).toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" })}` : "mai effettuato"}
        </div>
      )}
      {acc.exists && (
        <div data-testid="area-riservata-invii">
          <div className="text-[11px] font-bold uppercase tracking-widest text-amber-800 mb-1">Cronologia invii link di accesso</div>
          {(acc.invii || []).length === 0 ? (
            <div className="text-xs text-slate-500" data-testid="area-riservata-invii-empty">Nessun invio effettuato.</div>
          ) : (
            <div className="max-h-40 overflow-y-auto rounded-md border border-slate-300 bg-white divide-y divide-slate-200">
              {acc.invii.map((iv, i) => (
                <div key={`${iv.at}-${i}`} className="flex items-center gap-3 px-3 py-1.5 text-xs text-slate-700" data-testid={`area-riservata-invio-${i}`}>
                  {iv.via === "whatsapp" ? <MessageCircle className="h-3 w-3 text-[#25D366] shrink-0" /> : <Send className="h-3 w-3 text-amber-600 shrink-0" />}
                  <span className="font-mono whitespace-nowrap">{new Date(iv.at).toLocaleDateString("it-IT")} · {new Date(iv.at).toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" })}</span>
                  <span className="truncate">{iv.via === "whatsapp" ? `WhatsApp · ${iv.numero || ""}` : iv.email}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
